import type { CSSProperties, ReactNode } from "react";
import { useEffect, useMemo, useState } from "react";
import {
  Navigate,
  Route,
  Routes,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import type {
  PayrollDashboard,
  PayrollEmployeeDetail,
} from "@triedge/payroll-domain";
import {
  PayrollDashboardPage,
  PayrollEmployeeDetailPage,
  type PayrollEmployeeProfileDraft,
  type PayrollUiEmployee,
  type PayrollUiRosterEmployee,
} from "@triedge/payroll-ui";
import { LockIcon } from "lucide-react";
import { readDomainRouterErrorMessage } from "../../../state/domainRouterError";
import { SupportErrorMessage } from "../../../components/overlays/EngineeringSupportBanner";
import {
  useDomainRouterMutation,
  useDomainRouterQuery,
} from "../../../state/domainRouterHooks";
import {
  hasCognitoPermission,
  resolveCognitoProfile,
} from "../../../utils/cognito-profile";

const SourceFaithfulFrame = ({
  children,
  testId,
}: {
  children: ReactNode;
  testId: string;
}) => (
  <main
    data-testid={testId}
    data-route-ready="true"
    data-route-family="source-faithful"
    data-extracted-from="source-faithful:school-payroll-2.0-magp"
    className="source-faithful-shell font-body !p-0 lg:!p-6"
    style={
      { "--ystnj-source-faithful-max-width": "1480px" } as CSSProperties
    }
  >
    <div className="source-faithful-container">{children}</div>
  </main>
);

const resolvePayrollAdminCapability = (
  profile: ReturnType<typeof resolveCognitoProfile>,
): boolean =>
  profile?.persona === "administrator" ||
  profile?.isSuperAdmin === true;

const AccessDenied = ({
  testId,
  mode,
}: {
  testId: string;
  mode: "restricted" | "session-drift";
}) => (
  <SourceFaithfulFrame testId={testId}>
    <div className="mx-auto max-w-[960px] rounded-[16px] border border-amber-200 bg-amber-50 p-8 text-amber-900">
      <div className="flex items-start gap-3">
        <LockIcon className="mt-0.5 h-5 w-5" />
        <div className="space-y-2">
          {mode === "session-drift" ? (
            <>
              <h2 className="text-lg font-semibold">
                Payroll admin access is out of sync.
              </h2>
              <p className="text-sm">
                Your session resolves to an administrator-capable persona, but
                the `perm_payroll_admin` claim is missing. Refresh the session
                after the payroll auth deploy so this route can execute with the
                canonical backend-minted payroll permission.
              </p>
            </>
          ) : (
            <>
              <h2 className="text-lg font-semibold">
                Payroll access is restricted.
              </h2>
              <p className="text-sm">
                This route requires the `perm_payroll_admin` permission claim.
                Administrators, owners, and super-administrators can access payroll;
                staff members cannot.
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  </SourceFaithfulFrame>
);

const mapFullName = (fullName: string) => {
  const parts = fullName.trim().split(/\s+/);
  if (parts.length <= 1) {
    return {
      firstName: fullName,
      lastName: fullName,
    };
  }
  return {
    firstName: parts.slice(0, -1).join(" "),
    lastName: parts[parts.length - 1] ?? fullName,
  };
};

const mapDashboardEmployees = (
  dashboard: PayrollDashboard | null | undefined,
): PayrollUiRosterEmployee[] =>
  (dashboard?.employees ?? []).map((employee) => {
    const { firstName, lastName } = mapFullName(employee.fullName);
    return {
      id: employee.employeeRecordId,
      employeeId: employee.employeeId,
      firstName,
      lastName,
      employmentType: employee.employmentType,
      track: employee.track,
      status: employee.status,
      annualCompTotal: employee.annualCompTotal,
      positionCount: employee.track === "Both" ? 2 : 1,
    };
  });

const mapEmployeeDetail = (
  detail: PayrollEmployeeDetail | null | undefined,
): PayrollUiEmployee | undefined =>
  detail
    ? {
        ...detail.employee,
        deductions: detail.employee.deductionEntries,
      }
    : undefined;

const DashboardRoute = () => {
  const navigate = useNavigate();
  const profile = resolveCognitoProfile();
  const canAdmin = hasCognitoPermission(profile, "perm_payroll_admin");
  const isAdminCapable = resolvePayrollAdminCapability(profile);
  const [searchParams] = useSearchParams();
  const [selectedPeriodId, setSelectedPeriodId] = useState<string | undefined>(
    () => searchParams.get("periodId") ?? undefined,
  );
  const [dashboardOverride, setDashboardOverride] =
    useState<PayrollDashboard | null>(null);

  const dashboardQuery = useDomainRouterQuery("getPayrollDashboard", {
    periodId: selectedPeriodId,
  });
  const [switchPayrollPeriod, switchState] =
    useDomainRouterMutation("switchPayrollPeriod");
  const [submitForApproval, submitState] = useDomainRouterMutation(
    "submitPayrollPeriodForApproval",
  );
  const [unlockPeriod, unlockState] =
    useDomainRouterMutation("unlockPayrollPeriod");
  const [exportPeriod, exportState] =
    useDomainRouterMutation("exportPayrollPeriod");
  const [savePosition] = useDomainRouterMutation("upsertPayrollPosition");
  const [saveStipend] = useDomainRouterMutation("upsertPayrollStipend");
  const [saveProfile] =
    useDomainRouterMutation("upsertPayrollEmployeeProfile");

  useEffect(() => {
    if (dashboardQuery.data) {
      setDashboardOverride(null);
    }
  }, [dashboardQuery.data]);

  if (!canAdmin) {
    return (
      <AccessDenied
        testId={
          isAdminCapable
            ? "payroll-source-faithful-session-coherence-error"
            : "payroll-source-faithful-access-denied"
        }
        mode={isAdminCapable ? "session-drift" : "restricted"}
      />
    );
  }

  const dashboard = dashboardOverride ?? dashboardQuery.data;
  const selectedPeriod =
    dashboard?.periods.find((period) => period.id === dashboard.selectedPeriodId) ??
    null;

  if (dashboardQuery.isError && !dashboard) {
    return (
      <SourceFaithfulFrame testId="payroll-source-faithful-dashboard-error">
        <SupportErrorMessage className="mx-auto max-w-[960px]">
          <p className="text-sm font-semibold">Payroll dashboard failed to load.</p>
          <p className="mt-2 text-sm">
            {readDomainRouterErrorMessage(
              dashboardQuery.error,
              "Payroll dashboard is unavailable.",
            )}
          </p>
        </SupportErrorMessage>
      </SourceFaithfulFrame>
    );
  }

  if (!dashboard || !selectedPeriod) {
    return (
      <SourceFaithfulFrame testId="payroll-source-faithful-dashboard-loading">
        <div className="mx-auto max-w-[960px] rounded-[14px] border border-border bg-white p-6 text-sm text-text-secondary">
          Loading payroll dashboard…
        </div>
      </SourceFaithfulFrame>
    );
  }

  const applyDashboardResult = (
    next: PayrollDashboard | { dashboard: PayrollDashboard },
  ) => {
    setDashboardOverride("dashboard" in next ? next.dashboard : next);
  };

  const isBusy =
    switchState.isLoading ||
    submitState.isLoading ||
    unlockState.isLoading ||
    exportState.isLoading;

  return (
    <SourceFaithfulFrame testId="payroll-source-faithful-root">
      <PayrollDashboardPage
        periods={dashboard.periods}
        selectedPeriodId={dashboard.selectedPeriodId}
        employees={mapDashboardEmployees(dashboard)}
        providerTemplateRefs={dashboard.providerTemplateRefs}
        isBusy={isBusy}
        onSelectPeriod={async (periodId) => {
          setSelectedPeriodId(periodId);
          const result = await switchPayrollPeriod({
            input: {
              periodId,
            },
          });
          applyDashboardResult(result);
        }}
        onOpenEmployee={(employeeId) =>
          navigate(
            `/school/payroll/employee/${employeeId}?periodId=${dashboard.selectedPeriodId}`,
          )
        }
        onSubmitForApproval={async (notes) => {
          const result = await submitForApproval({
            input: {
              periodId: selectedPeriod.id,
              notes: notes || undefined,
            },
          });
          applyDashboardResult(result);
        }}
        onUnlockPeriod={async (reason) => {
          const result = await unlockPeriod({
            input: {
              periodId: selectedPeriod.id,
              reason: reason || undefined,
            },
          });
          applyDashboardResult(result);
        }}
        onExportPeriod={async (input) => {
          const result = await exportPeriod({
            input: {
              periodId: selectedPeriod.id,
              providerTemplateId: input.providerTemplateId,
              exportFormat: input.exportFormat,
            },
          });
          applyDashboardResult(result);
        }}
        onAddPosition={async (employeeId, position) => {
          await savePosition({
            input: {
              employeeId,
              periodId: selectedPeriod.id,
              position,
            },
          });
          void dashboardQuery.refetch();
        }}
        onAddStipend={async (employeeId, stipend) => {
          await saveStipend({
            input: {
              employeeId,
              periodId: selectedPeriod.id,
              stipend,
            },
          });
          void dashboardQuery.refetch();
        }}
        onSuspendEmployee={async (employeeId, _reason) => {
          await saveProfile({
            input: {
              employeeId,
              periodId: selectedPeriod.id,
              profile: {
                status: "On Leave",
                leaveInfo: {
                  leaveType: "Medical",
                  startDate: selectedPeriod.startDate,
                  expectedReturnDate: selectedPeriod.endDate,
                  partialPayPercentage: 0,
                },
              },
            },
          });
          void dashboardQuery.refetch();
        }}
      />
    </SourceFaithfulFrame>
  );
};

const DetailRoute = () => {
  const profile = resolveCognitoProfile();
  const canAdmin = hasCognitoPermission(profile, "perm_payroll_admin");
  const isAdminCapable = resolvePayrollAdminCapability(profile);
  const { employeeId } = useParams<{ employeeId: string }>();
  const [searchParams] = useSearchParams();
  const periodId = searchParams.get("periodId") ?? undefined;

  const detailQuery = useDomainRouterQuery(
    "getPayrollEmployeeDetail",
    {
      employeeId: employeeId ?? "",
      periodId,
    },
    {
      skip: !employeeId,
    },
  );
  const [saveProfile] =
    useDomainRouterMutation("upsertPayrollEmployeeProfile");
  const [savePosition] = useDomainRouterMutation("upsertPayrollPosition");
  const [saveStipend] = useDomainRouterMutation("upsertPayrollStipend");
  const [saveAdjustment] = useDomainRouterMutation("upsertPayrollAdjustment");
  const [saveExternalTuition] = useDomainRouterMutation(
    "upsertPayrollExternalTuitionPayment",
  );
  const [saveDeduction] = useDomainRouterMutation("upsertPayrollDeductionEntry");

  if (!canAdmin) {
    return (
      <AccessDenied
        testId={
          isAdminCapable
            ? "payroll-source-faithful-session-coherence-error"
            : "payroll-source-faithful-access-denied"
        }
        mode={isAdminCapable ? "session-drift" : "restricted"}
      />
    );
  }

  if (!employeeId) {
    return <Navigate to="/school/payroll" replace />;
  }

  const detail = detailQuery.data;
  if (detailQuery.isLoading && !detail) {
    return (
      <SourceFaithfulFrame testId="payroll-source-faithful-detail-loading">
        <div className="mx-auto max-w-[960px] rounded-[14px] border border-border bg-white p-6 text-sm text-text-secondary">
          Loading payroll detail…
        </div>
      </SourceFaithfulFrame>
    );
  }

  if (!detail) {
    return (
      <SourceFaithfulFrame testId="payroll-source-faithful-detail-error">
        <SupportErrorMessage className="mx-auto max-w-[960px]">
          <p className="text-sm font-semibold">Payroll detail failed to load.</p>
          <p className="mt-2 text-sm">
            {readDomainRouterErrorMessage(
              detailQuery.error,
              "Payroll detail is unavailable.",
            )}
          </p>
        </SupportErrorMessage>
      </SourceFaithfulFrame>
    );
  }

  const mappedEmployee = mapEmployeeDetail(detail);
  if (!mappedEmployee) {
    return <Navigate to="/school/payroll" replace />;
  }

  return (
    <SourceFaithfulFrame testId="payroll-source-faithful-detail">
      <PayrollEmployeeDetailPage
        employee={mappedEmployee}
        currentPeriod={detail.selectedPeriod}
        backHref={`/school/payroll?periodId=${detail.selectedPeriod.id}`}
        onPersistPosition={async (position) => {
          const result = await savePosition({
            input: {
              employeeId: detail.employee.id,
              periodId: detail.selectedPeriod.id,
              position,
            },
          });
          return mapEmployeeDetail(result);
        }}
        onPersistStipend={async (stipend) => {
          const result = await saveStipend({
            input: {
              employeeId: detail.employee.id,
              periodId: detail.selectedPeriod.id,
              stipend,
            },
          });
          return mapEmployeeDetail(result);
        }}
        onPersistAdjustment={async (adjustment) => {
          const result = await saveAdjustment({
            input: {
              employeeId: detail.employee.id,
              periodId: detail.selectedPeriod.id,
              adjustment,
            },
          });
          return mapEmployeeDetail(result);
        }}
        onPersistExternalTuitionPayment={async (externalTuitionPayment) => {
          const result = await saveExternalTuition({
            input: {
              employeeId: detail.employee.id,
              periodId: detail.selectedPeriod.id,
              externalTuitionPayment,
            },
          });
          return mapEmployeeDetail(result);
        }}
        onPersistDeduction={async (deductionEntry) => {
          const result = await saveDeduction({
            input: {
              employeeId: detail.employee.id,
              periodId: detail.selectedPeriod.id,
              deductionEntry,
            },
          });
          return mapEmployeeDetail(result);
        }}
        onPersistEmployeeProfile={async (
          profileDraft: PayrollEmployeeProfileDraft,
        ) => {
          const result = await saveProfile({
            input: {
              employeeId: detail.employee.id,
              periodId: detail.selectedPeriod.id,
              profile: {
                email:
                  profileDraft.email === null
                    ? undefined
                    : profileDraft.email ?? undefined,
                status: profileDraft.status ?? detail.employee.status,
                leaveInfo:
                  profileDraft.leaveInfo === undefined
                    ? detail.employee.leaveInfo
                    : profileDraft.leaveInfo,
              },
            },
          });
          return mapEmployeeDetail(result);
        }}
      />
    </SourceFaithfulFrame>
  );
};

export const SchoolPayrollSourceFaithfulRoutes = () => (
  <Routes>
    <Route index element={<DashboardRoute />} />
    <Route path="employee/:employeeId" element={<DetailRoute />} />
    <Route path="*" element={<Navigate to="." replace />} />
  </Routes>
);
